/**
 * County- and city-level GHG + population for Analysis map (per-capita choropleth).
 * GHG sectors from fetchGHGInventoryData for the selected year (live place GHG page).
 */

import { inventoryGeofipsNumeric } from './ghgInventoryGeofipsCanonical';
import { fetchGhgRankingPlaces } from './ghgEmissionsRankingsService';
import { supabaseClient } from './supabase';

const PAGE_SIZE = 1000;

export type CountyGhgMapMetric =
  | 'total'
  | 'transport_ldv_hdv'
  | 'ldv'
  | 'hdv'
  | 'electricity'
  | 'natural_gas'
  | 'agriculture'
  | 'offroad'
  | 'waste';

export const COUNTY_GHG_MAP_METRIC_OPTIONS: { value: CountyGhgMapMetric; label: string }[] = [
  { value: 'total', label: 'Total (all sectors)' },
  { value: 'transport_ldv_hdv', label: 'Transportation (LDV + HDV)' },
  { value: 'ldv', label: 'Light-duty vehicles' },
  { value: 'hdv', label: 'Heavy-duty vehicles' },
  { value: 'electricity', label: 'Electricity (res. + com.)' },
  { value: 'natural_gas', label: 'Natural gas (res. + com.)' },
  { value: 'agriculture', label: 'Agriculture' },
  { value: 'offroad', label: 'Off-road' },
  { value: 'waste', label: 'Waste' },
];

export interface CountyGhgMapRow {
  geofips: number;
  /** Census COUNTYFP (3 digits, e.g. "037") for tract GeoJSON join */
  countyFp3: string;
  location_name: string;
  population: number;
  total_mt: number;
  ldv_mt: number;
  hdv_mt: number;
  elec_mt: number;
  ng_mt: number;
  ag_mt: number;
  offroad_mt: number;
  waste_mt: number;
}

export interface CountyGhgMapPayload {
  year: number;
  rows: CountyGhgMapRow[];
  error: string | null;
}

function num(v: unknown): number {
  if (v == null) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function rankingRowsToMapRows(
  rankingRows: Array<{
    geofips: number;
    location_name: string;
    population: number;
    total_mt: number;
    ldv_mt: number;
    hdv_mt: number;
    elec_mt: number;
    ng_mt: number;
    ag_mt: number;
    offroad_mt: number;
    waste_mt: number;
  }>,
  isCounty: boolean
): CountyGhgMapRow[] {
  return rankingRows.map((g) => ({
    geofips: g.geofips,
    countyFp3: isCounty ? countyFpFromGeofips(g.geofips) : '',
    location_name: g.location_name,
    population: g.population,
    total_mt: g.total_mt,
    ldv_mt: g.ldv_mt,
    hdv_mt: g.hdv_mt,
    elec_mt: g.elec_mt,
    ng_mt: g.ng_mt,
    ag_mt: g.ag_mt,
    offroad_mt: g.offroad_mt,
    waste_mt: g.waste_mt,
  }));
}

function countyFpFromGeofips(geofips: number): string {
  const n = geofips % 1000;
  return String(n).padStart(3, '0');
}

/** Sector total (MT CO₂e) for the chosen map metric. */
export function sectorMtForMetric(row: CountyGhgMapRow, metric: CountyGhgMapMetric): number {
  switch (metric) {
    case 'total':
      return row.total_mt;
    case 'transport_ldv_hdv':
      return row.ldv_mt + row.hdv_mt;
    case 'ldv':
      return row.ldv_mt;
    case 'hdv':
      return row.hdv_mt;
    case 'electricity':
      return row.elec_mt;
    case 'natural_gas':
      return row.ng_mt;
    case 'agriculture':
      return row.ag_mt;
    case 'offroad':
      return row.offroad_mt;
    case 'waste':
      return row.waste_mt;
    default:
      return row.total_mt;
  }
}

/** Metric tons CO₂e per person (inventory uses MT). */
export function perCapitaForMetric(row: CountyGhgMapRow, metric: CountyGhgMapMetric): number | null {
  if (row.population <= 0) return null;
  const mt = sectorMtForMetric(row, metric);
  if (!Number.isFinite(mt)) return null;
  return mt / row.population;
}

export type CountyGhgMapDisplayMode = 'per_capita' | 'total_emissions';

/**
 * Positive numeric value for map quintile coloring: per capita (t CO₂e / person) or sector total (MT CO₂e).
 */
export function choroplethValueForMap(
  row: CountyGhgMapRow,
  metric: CountyGhgMapMetric,
  mode: CountyGhgMapDisplayMode
): number | null {
  if (mode === 'per_capita') {
    return perCapitaForMetric(row, metric);
  }
  const mt = sectorMtForMetric(row, metric);
  if (!Number.isFinite(mt) || mt <= 0) return null;
  return mt;
}

export async function fetchCaliforniaCountyGhgForMap(year: number): Promise<CountyGhgMapPayload> {
  const payload = await fetchGhgRankingPlaces(year, 'County');
  if (payload.error) {
    return { year, rows: [], error: payload.error };
  }
  const rows = rankingRowsToMapRows(payload.rows, true);
  rows.sort((a, b) => a.geofips - b.geofips);
  return { year, rows, error: null };
}

export interface CountyCityGhgMapPayload {
  year: number;
  countyRows: CountyGhgMapRow[];
  cityRows: CountyGhgMapRow[];
  error: string | null;
}

/** California cities (geofips 6xxxxx) for the same per-capita map metrics as counties. */
export async function fetchCaliforniaCityGhgForMap(year: number): Promise<CountyGhgMapPayload> {
  const payload = await fetchGhgRankingPlaces(year, 'City');
  if (payload.error) {
    return { year, rows: [], error: payload.error };
  }
  const rows = rankingRowsToMapRows(payload.rows, false);
  rows.sort((a, b) => a.geofips - b.geofips);
  return { year, rows, error: null };
}

export async function fetchCaliforniaCountyCityGhgForMap(year: number): Promise<CountyCityGhgMapPayload> {
  const [c, ct] = await Promise.all([fetchCaliforniaCountyGhgForMap(year), fetchCaliforniaCityGhgForMap(year)]);
  const err = c.error ?? ct.error;
  return {
    year,
    countyRows: c.rows,
    cityRows: ct.rows,
    error: err,
  };
}
